import React from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';

import { useIntl } from '@tink/core/src/components/providers/LanguageProvider/useIntl';
import { DD, DL, DT } from '@tink/core/src/components/atoms/DescriptionList/DescriptionList';
import { Header } from '../../organisms/Header/Header';
import { ScreenWrapper } from '../../atoms/ScreenWrapper/ScreenWrapper';

import { ContentBody } from '../../atoms/ContentBody/ContentBody';
import { H1 } from '../../atoms/Heading/Heading';
import { PageView } from '../../../models/analytics';
import { messages } from './messages';

/**
 * In Cypress we use an internal route as the redirect_uri.
 * That way we can see what values customers would get when returning from Tink Link.
 * RedirectedValues is only for internal testing purposes.
 */
const RedirectedValues = ({ rawQuery }: { rawQuery: string }) => {
  return (
    <DL>
      {rawQuery.split('&').map((pair) => {
        const [key, value] = pair.split('=');

        return (
          <React.Fragment key={key}>
            <DT>{key}</DT>
            <DD role="definition">
              <pre style={{ wordBreak: 'break-all', whiteSpace: 'normal' }}>
                {decodeURIComponent(value)}
              </pre>
            </DD>
          </React.Fragment>
        );
      })}
    </DL>
  );
};

export type NotFoundPageProps = {
  query?: string;
  isGenericError?: boolean;
};

export const NotFoundPage = ({
  query = window.location.search,
  isGenericError,
}: NotFoundPageProps) => {
  const intl = useIntl();
  const params = new URLSearchParams(query);
  const error = params.get('error');

  return (
    <>
      <Helmet>
        <title>{intl.formatMessage(messages.notFound)}</title>
        <meta name="description" content={intl.formatMessage(messages.notFound)} />
      </Helmet>
      <ScreenWrapper>
        <Header onBack={undefined} hideCloseButton pageView={PageView.NOT_FOUND_SCREEN} />
        <ContentBody centerHorizontally>
          {isGenericError ? (
            <H1 color="errorTextColor">
              <FormattedMessage {...messages.generalError} />
            </H1>
          ) : (
            <H1>
              <FormattedMessage {...messages.notFound} />
            </H1>
          )}
          {error && <RedirectedValues rawQuery={query.replace(/\?/g, '')} />}
        </ContentBody>
      </ScreenWrapper>
    </>
  );
};
